import React from "react";
import classNames from "classnames";
import PropTypes from "prop-types";
import { makeStyles } from "@mui/styles";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import Button from "@mui/material/Button";
import Drawer from "@mui/material/Drawer";
import Menu from "@mui/icons-material/Menu";
import styles from '../../styles/headerStyle.js';
import { useTranslation } from "react-i18next";
import { Box } from '@mui/material';
import LanguageMenu from '../../components/LanguageMenu.jsx';
// redux state not needed here for header UI
import { Link as RouterLink } from 'react-router-dom';

const useStyles = makeStyles(styles);

export default function Header(props) {
  const { i18n } = useTranslation();
  const isRTL = i18n.dir();
  const currentLang = i18n.language;
  const classes = useStyles();

  // Use Ge'ez script for Amharic and Tigrinya
  const logoText = (currentLang === 'am' || currentLang === 'ti') ? 'ፕሉክ' : 'PLUKK';
  const [mobileOpen, setMobileOpen] = React.useState(false);

  // Language handled by LanguageMenu; keep header state focused on UI only

  // (Language initialization is handled globally in App and via LanguageMenu)

  React.useEffect(() => {
    if (props.changeColorOnScroll) {
      window.addEventListener("scroll", headerColorChange);
    }
    return function cleanup() {
      if (props.changeColorOnScroll) {
        window.removeEventListener("scroll", headerColorChange);
      }
    };
  });
  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };
  const headerColorChange = () => {
    const { color, changeColorOnScroll } = props;
    const windowsScrollTop = window.pageYOffset;
    if (windowsScrollTop > changeColorOnScroll.height) {
      document.body
        .getElementsByTagName("header")[0]
        .classList.remove(classes[color]);
      document.body
        .getElementsByTagName("header")[0]
        .classList.add(classes[changeColorOnScroll.color]);
    } else {
      document.body
        .getElementsByTagName("header")[0]
        .classList.add(classes[color]);
      document.body
        .getElementsByTagName("header")[0]
        .classList.remove(classes[changeColorOnScroll.color]);
    }
  };
  const { color, rightLinks, leftLinks, fixed, absolute } = props;
  const brandName = props.brandName || logoText;
  const logoUrl = props.logoUrl;
  const brandHref = props.brandHref || '/';
  const appBarClasses = classNames({
    [classes.appBar]: true,
    [classes[color]]: color,
    [classes.absolute]: absolute,
    [classes.fixed]: fixed
  });
  // Determine text color based on header background
  const isTransparent = color === 'transparent';
  const isDark = color === 'dark' || isTransparent;
  const textColor = isDark ? '#fff' : '#000';
  const navMode = props.navMode || (color === 'white' ? 'light' : 'dark');
  const navVars = navMode === 'light'
    ? {
        '--nav-hover-bg': 'rgba(0,0,0,0.05)',
        '--nav-active-bg': 'rgba(0,0,0,0.08)',
        '--nav-underline': 'rgba(15,23,42,0.9)',
        '--nav-menu-hover': 'rgba(15,23,42,0.06)',
      }
    : {
        '--nav-hover-bg': 'rgba(255,255,255,0.10)',
        '--nav-active-bg': 'rgba(255,255,255,0.16)',
        '--nav-underline': '#ffffff',
        '--nav-menu-hover': 'rgba(15,23,42,0.06)',
      };

  const brandComponent = (
    <Button
      component={RouterLink}
      to={brandHref}
      className={classes.title}
      aria-label={`${brandName} home`}
      disableRipple
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.75,
        padding: 0,
        minWidth: 'auto',
        '&:hover': {
          backgroundColor: 'transparent',
        },
      }}
    >
      {/* Icon/Logo mark */}
      {logoUrl ? (
        <Box
          component="img"
          src={logoUrl}
          alt={brandName}
          sx={{ height: 32, width: 'auto', borderRadius: 1.5, objectFit: 'contain' }}
        />
      ) : (
        <Box
          sx={{
            width: 32,
            height: 32,
            borderRadius: 1.5,
            bgcolor: textColor,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Box
            component="span"
            sx={{
              fontSize: 18,
              fontWeight: 900,
              color: isDark ? '#000' : '#fff',
              fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "Segoe UI", Roboto, sans-serif',
            }}
          >
            P
          </Box>
        </Box>
      )}
      {/* Text logo */}
      <Box
        component="span"
        sx={{
          fontSize: 26,
          fontWeight: 900,
          letterSpacing: -0.5,
          color: textColor,
          fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "Segoe UI", Roboto, sans-serif',
        }}
      >
        {brandName}
      </Box>
    </Button>
  );
  return (
    <AppBar className={appBarClasses}>
      <Toolbar
        className={classes.container}
        style={{ direction: isRTL === 'rtl' ? 'rtl' : 'ltr', ...navVars }}
      >
        {leftLinks !== undefined ? brandComponent : null}
        <div className={isRTL === 'rtl' ? null : classes.flex}>
          {leftLinks !== undefined ? (
            <Box component="div" sx={{ display: { sm: 'none', md: 'block' } }} >
              {leftLinks}
            </Box>
          ) : (
            brandComponent
          )}
        </div>

        <div style={{ direction: isRTL === 'rtl' ? 'rtl' : 'ltr' }}>
          <Box component="div" sx={{ display: { sm: 'none', md: 'block', xs: 'none' } }} >
              {rightLinks}
          </Box>
        </div>
        <Box component="div" sx={{ display: { md: 'none', xs: 'flex' }, alignItems:'center', gap:0.5 }} >
          <LanguageMenu compact />
          <IconButton color="inherit" aria-label="open drawer" onClick={handleDrawerToggle}>
            <Menu />
          </IconButton>
        </Box>
      </Toolbar>
      <Box component="div" sx={{ display: { md: 'none', xs: 'block' } }} >
        <Drawer
          variant="temporary"
          anchor={"right"}
          open={mobileOpen}
          classes={{
            paper: classes.drawerPaper
          }}
          onClose={handleDrawerToggle}
        >
          <div className={classes.appResponsive}>
            {leftLinks}
            {rightLinks}
          </div>
        </Drawer>
      </Box>
    </AppBar>
  );
}

Header.defaultProp = {
  color: "white"
};

Header.propTypes = {
  color: PropTypes.oneOf([
    "primary",
    "info",
    "success",
    "warning",
    "danger",
    "transparent",
    "white",
    "rose",
    "dark"
  ]),
  rightLinks: PropTypes.node,
  leftLinks: PropTypes.node,
  brand: PropTypes.string,
  fixed: PropTypes.bool,
  absolute: PropTypes.bool,
  changeColorOnScroll: PropTypes.shape({
    height: PropTypes.number.isRequired,
    color: PropTypes.oneOf([
      "primary",
      "info",
      "success",
      "warning",
      "danger",
      "transparent",
      "white",
      "rose",
      "dark",
      "header"
    ]).isRequired
  })
};
